import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { finalize, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import {
	ConfirmationDialogComponent,
	ConfirmationDialogData,
} from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { SnackBarService } from 'src/app/shared/components/snack-bar/snack-bar.service';
import {
	UPLOAD_EXTENSIONS,
	UPLOAD_MAX_FILES,
} from 'src/app/shared/constants/constants';
import { UPLOAD_MAX_FILES_MESSAGE } from 'src/app/shared/constants/error-message';
import { TableErrors } from 'src/app/shared/models/table-errors.model';
import { environment } from 'src/environments/environment';

import { UploadStatus } from '../../../../shared/enums/batch-actions.enum';
import {
	BatchActionInfo,
	BatchActionDocument,
	DownloadFilesRequest,
	DownloadFilesResponse,
	UPLOAD_STATUS_LABEL,
} from '../../models/batch-actions.model';
import {
	cancelOrgBatchActionStart,
	fetchOrgBatchActionInfoStart,
} from '../../state/organizations.actions';
import {
	selectOrgBatchActionInfo,
	selectOrgBatchActionLoadStatus,
} from '../../state/organizations.selector';
import { LoadStatus } from '../../state/organizations.state';
import { BeneficiaryAttachDialogComponent } from '../../views/beneficiary-view/beneficiary-view.component';

@Component({
	selector: 'app-batch-action-info',
	styleUrls: ['./batch-action-info.component.scss'],
	templateUrl: './batch-action-info.component.html',
})
export class BatchActionInfoComponent implements OnInit, OnDestroy {
	@ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

	LoadStatus = LoadStatus;
	acceptedExtensionsString: string = '';
	alertRef!: MatDialogRef<BeneficiaryAttachDialogComponent>;
	batchAction?: BatchActionInfo;
	batchAction$: Observable<BatchActionInfo | null>;
	deletingStates: { [fileId: string]: boolean } = {};
	downloadingStates: { [fileId: string]: boolean } = {};
	extensions = UPLOAD_EXTENSIONS;
	readonly extensionsMessage = `Extensões: ${this.extensions.join(', ')}`;
	hasExtensionError: boolean = false;
	isDownloadingAll: boolean = false;
	isDraggingOver = false;
	isUploading: boolean = false;
	loadStatus$: Observable<LoadStatus>;
	orgId: string = '';
	selectedFiles: File[] = [];

	tablesErros: TableErrors[] = [];
	uploadId: string = '';
	uploadStatus = UploadStatus;

	private readonly apiUrl = environment.apiUrl;
	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly store: Store,
		private readonly http: HttpClient,
		private readonly snackBarService: SnackBarService,
		private readonly dialog: MatDialog,
	) {
		this.acceptedExtensionsString = this.extensions
			.map((ext) => `.${ext}`)
			.join(',');

		this.batchAction$ = this.store.select(selectOrgBatchActionInfo);
		this.loadStatus$ = this.store.select(selectOrgBatchActionLoadStatus);
	}

	cancelSchedule(): void {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			data: {
				confirmButtonText: 'Cancelar transferência',
				message:
					'Tem certeza de que deseja cancelar esta transferência agendada? Esta ação não pode ser desfeita.',
				title: 'Cancelar transferência agendada',
			} as ConfirmationDialogData,
			disableClose: true,
		});
		dialogRef.afterClosed().subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.store.dispatch(
					cancelOrgBatchActionStart({
						id: this.uploadId,
					}),
				);
			}
		});
	}

	deleteDocument(doc: BatchActionDocument): void {
		if (!doc.id) return void 0;
		this.deletingStates[doc.id] = true;
		const url = `${this.apiUrl}/upload/${this.uploadId}/delete/${doc.id}`;

		this.http
			.delete(url)
			.pipe(finalize(() => (this.deletingStates[doc.id] = false)))
			.subscribe({
				error: (err: HttpErrorResponse) => {
					this.snackBarService.showSnack(
						`Erro ao excluir documento: ${err.error?.message || err.message}`,
						'error',
					);
				},
				next: () => {
					this.snackBarService.showSnack(
						`Documento '${doc.name}' excluído com sucesso.`,
						'success',
					);
					this.fetchBatchActionDetails();
				},
			});

		return void 0;
	}

	downloadAllDocuments(): void {
		if (!this.batchAction?.files || this.batchAction.files.length === 0) {
			return void 0;
		}

		const fileIds = this.batchAction.files.map((doc) => doc.id);

		if (fileIds.length > 0) {
			this.isDownloadingAll = true;
			this.downloadFiles(fileIds, () => (this.isDownloadingAll = false));
		}

		return void 0;
	}

	downloadDocument(doc: BatchActionDocument): void {
		if (!doc.id) return void 0;
		this.downloadingStates[doc.id] = true;
		this.downloadFiles(
			[doc.id],
			() => (this.downloadingStates[doc.id] = false),
		);

		return void 0;
	}

	fetchBatchActionDetails(): void {
		this.store.dispatch(fetchOrgBatchActionInfoStart({ id: this.uploadId }));
		this.store
			.select(selectOrgBatchActionInfo)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: () => {
					this.batchAction = undefined;
				},
				next: (ba) => {
					this.batchAction = ba ? { ...ba, id: this.uploadId } : undefined;
					if (ba) {
						this.processErrors(this.batchAction);
					}
				},
			});
	}

	formatBytes(bytes: number, decimals = 2): string {
		if (!+bytes) return '0 Bytes';
		const k = 1024;
		const dm = decimals < 0 ? 0 : decimals;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));

		return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	ngOnInit(): void {
		this.activatedRoute.params
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(({ id, uId }) => {
				if (id && uId) {
					this.orgId = id;
					this.uploadId = uId;
					this.fetchBatchActionDetails();
				} else {
					this.snackBarService.showSnack(
						'IDs de organização ou upload faltando na rota.',
						'error',
					);
				}
			});
	}

	onDragLeave(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		const target = event.relatedTarget as Node | null;

		if (!target || !(event.currentTarget as Node).contains(target)) {
			this.isDraggingOver = false;
		}
	}

	onDragOver(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDraggingOver = true;
	}

	onDrop(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDraggingOver = false;
		if (event.dataTransfer?.files) {
			this.addFilesToList(event.dataTransfer.files);
		}
	}

	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;

		if (input.files) {
			this.addFilesToList(input.files);
			input.value = '';
		}
	}

	processErrors(ba: BatchActionInfo | undefined): void {
		if (!ba?.description?.data) {
			this.tablesErros = [];

			return void 0;
		}

		const newTablesErros: TableErrors[] = [];

		for (const error of ba.description.data) {
			const foundRecord = newTablesErros.find(
				(data) => data.name === error.table,
			);
			const rowErrors = error.errors?.split(',').map((e) => e.trim());

			if (rowErrors && rowErrors.length > 0 && rowErrors[0] !== '') {
				if (foundRecord) {
					foundRecord.errors.push({
						detailedErros: rowErrors,
						row: error.row,
					});
				} else {
					newTablesErros.push(
						new TableErrors(error.table, [
							{ detailedErros: rowErrors, row: error.row },
						]),
					);
				}
			}
		}
		this.tablesErros = newTablesErros;

		return void 0;
	}

	removeSelectedFile(index: number): void {
		if (index >= 0 && index < this.selectedFiles.length) {
			this.selectedFiles.splice(index, 1);
		}
	}

	statusLabel(status: UploadStatus): string {
		return (
			UPLOAD_STATUS_LABEL.find((us) => us.status === status)?.label ??
			'Desconhecido'
		);
	}

	triggerDownload(url: string, filename: string): void {
		const tagA = document.createElement('a');
		tagA.href = url;
		tagA.download = filename;
		document.body.appendChild(tagA);
		tagA.click();
		document.body.removeChild(tagA);
	}

	triggerFileInputClick(): void {
		this.fileInput.nativeElement.click();
	}

	uploadSelectedFiles(): void {
		if (this.selectedFiles.length === 0 || !this.orgId || !this.uploadId) {
			return void 0;
		}

		const formData = new FormData();
		this.selectedFiles.forEach((file) => {
			formData.append('files', file, file.name);
		});

		this.isUploading = true;
		const url = `${this.apiUrl}/upload/${this.orgId}/massive-operation-file/${this.uploadId}`;

		this.http
			.post(url, formData)
			.pipe(finalize(() => (this.isUploading = false)))
			.subscribe({
				error: (err: HttpErrorResponse) => {
					this.snackBarService.showSnack(
						`Erro ao enviar arquivos: ${err.error?.message || err.message}`,
						'error',
					);
				},
				next: () => {
					this.snackBarService.showSnack(
						'Arquivos enviados com sucesso!',
						'success',
					);
					this.selectedFiles = [];
					this.fetchBatchActionDetails();
				},
			});

		return void 0;
	}

	private addFilesToList(files: FileList): void {
		const newFiles = Array.from(files);
		const allowedFiles: File[] = [];
		let extensionError = false;

		if (this.selectedFiles.length + newFiles.length > UPLOAD_MAX_FILES) {
			this.alertRef = this.dialog.open(BeneficiaryAttachDialogComponent, {
				data: UPLOAD_MAX_FILES_MESSAGE,
				disableClose: true,
				maxWidth: undefined,
			});
		}

		newFiles.forEach((file) => {
			const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? '';

			if (this.extensions.includes(fileExtension)) {
				if (
					!this.selectedFiles.some(
						(existingFile) =>
							existingFile.name === file.name &&
							existingFile.size === file.size,
					)
				) {
					allowedFiles.push(file);
				}
			} else {
				extensionError = true;
			}
		});

		this.selectedFiles.push(...allowedFiles);

		if (extensionError) {
			this.alertRef = this.dialog.open(BeneficiaryAttachDialogComponent, {
				data: this.extensionsMessage,
				disableClose: true,
				maxWidth: undefined,
			});
		}

		return void 0;
	}

	private downloadFiles(fileIds: string[], onComplete: () => void): void {
		const request: DownloadFilesRequest = { fileIds };
		const url = `${this.apiUrl}/upload/${this.uploadId}/download`;

		this.http
			.post<DownloadFilesResponse>(url, request)
			.pipe(finalize(onComplete))
			.subscribe({
				error: (err: HttpErrorResponse) => {
					this.snackBarService.showSnack(
						`Erro ao obter links para download: ${
							err.error?.message || err.message
						}`,
						'error',
					);
				},
				next: (response) => {
					if (response.files && response.files.length > 0) {
						response.files.forEach((file, index) => {
							setTimeout(() => {
								this.triggerDownload(file.url, file.name);
							}, index * 100); // Delay each download by 1 second * index
						});
					} else {
						this.snackBarService.showSnack(
							'Nenhum arquivo encontrado para download.',
							'warn',
						);
					}
				},
			});
	}
}
